// Interface pour les suggestions
interface SearchSuggestion {
  type: 'user' | 'post';
  text: string;
  data?: any; // Données supplémentaires (nom d'utilisateur, photo, etc.)
}

import { Component, OnInit, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { FirebaseTSFirestore, Limit, OrderBy, Where } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { UserData } from '../../models/user.model';
import { AppComponent } from 'src/app/app.component';
import { MatDialog } from '@angular/material/dialog';
import { CreatePostComponent } from 'src/app/tools/create-post/create-post.component';
import { NavigationEnd, Router } from '@angular/router';
import { DeletePostDialogComponent } from 'src/app/delete-post-dialog/delete-post-dialog.component';
import { Message } from 'src/app/services/chat.service';
import { ImageModalComponent } from 'src/app/image-modal/image-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SearchService } from 'src/app/search.service';
import { BottomBarService } from 'src/app/services/bottom-bar.service';
import { PostData, CommentData, FirestoreTimestamp } from '../../models/post.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { UnreadMessagesService } from 'src/app/services/unread-messages.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';
import { ActivatedRoute } from '@angular/router';
import { collection, getDocs, query, getFirestore, where, limit } from 'firebase/firestore';

@Component({
  selector: 'app-post-feed',
  templateUrl: './post-feed.component.html',
  styleUrls: ['./post-feed.component.css']
})
export class PostFeedComponent implements OnInit {
  private component: AppComponent;
  userHasProfile: boolean = false; 
  firestore = new FirebaseTSFirestore();
  firestore2 = getFirestore();
  auth = new FirebaseTSAuth();
  posts: PostData [] = [];
  username = "";
  profession = "";
  currentUser : UserData;
  profilePhotoUrl = "https://firebasestorage.googleapis.com/v0/b/lenali-21.appspot.com/o/profile2.png?alt=media&token=a24d9c4a-5daf-48c0-a265-88c4b0371f3c";
  searchQuery: string = '';
  searchSuggestions: SearchSuggestion[] = [];
  showSuggestions: boolean = false;
  unreadMessagesCount: number = 0;
  userPosts: number = 0;
  isProfileView: boolean = false;
  isMessagesView: boolean = false;
  isNiagaIAView: boolean = false;

  @ViewChild('searchInput') searchInput: ElementRef;

  constructor(
    public bottomBarService: BottomBarService,
    private dialog: MatDialog, 
    private router: Router, 
    private authService: AuthService,
    private searchService: SearchService,
    private breakpointObserver: BreakpointObserver,
    private unreadMessagesService: UnreadMessagesService,
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
    private translationService: TranslationService,
    private route: ActivatedRoute
  ) { 
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');
  }

  ngOnInit(): void {
    // Écouter les changements de route pour détecter la vue profil et messages
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isProfileView = event.url.includes('/userprofile/');
        this.isMessagesView = event.url.includes('/messages');
        this.isNiagaIAView = event.url.includes('/niaga-ia');
        
        // Effacer la recherche si on ne navigue pas vers la page de recherche
        if (!event.url.includes('/search')) {
          this.clearSearch();
        }
      }
    });

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/']);  // Redirige vers HomeComponent si l'utilisateur n'est pas authentifié
      return;
    }

    this.router.navigate(['postfeed/feed']); // Sélectionne le fil d'actualité par défaut
    
    // Initialiser le profil utilisateur
    this.getUserProfile().then(() => {
      // Demander la permission pour le son après l'initialisation du profil
      this.unreadMessagesService.requestSoundPermission();
      
      // Charger les posts et écouter les changements
      this.getPosts();
      this.listenToChange();
    });

    // Vérifier la taille de l'écran au chargement
    this.checkViewport();

    // S'abonner aux changements de taille d'écran
    this.breakpointObserver.observe([
      '(max-width: 768px)'
    ]).subscribe(result => {
      this.isMobileView = result.matches;
    });

    // S'abonne à l'observable de la barre inférieure
    this.bottomBarService.showBottomBar$.subscribe(isVisible => {
      this.isBottomBarVisible = isVisible;
      this.changeDetectorRef.detectChanges();
    });

    // S'abonner au compteur de messages non lus
    this.unreadMessagesService.unreadCount$.subscribe(count => {
      console.log('Nombre de messages non lus reçu dans PostFeed:', count);
      this.unreadMessagesCount = count;
      // Forcer la détection des changements
      this.changeDetectorRef.detectChanges();
    });
  }

  clearSearch() {
    this.searchQuery = '';
    this.showSuggestions = false;
  }

  getUserProfile() {
    return new Promise<number>((resolved, rejected) => {
      const currentUser = this.auth.getAuth().currentUser;
      if (!currentUser) {
        console.error('Aucun utilisateur connecté');
        resolved(0);
        return;
      }

      this.firestore.listenToDocument({
        name: "Getting Document2",
        path: ["Users", currentUser.uid],
        onUpdate: (result) => {
          if (result) {
            this.userHasProfile = result.exists;
            if (result.exists) {
              this.currentUser = result.data() as UserData;
              this.profilePhotoUrl = this.currentUser.profilePhotoUrl || this.profilePhotoUrl;
              this.getUserPosts(); // Mettre à jour le nombre de posts
            }
          } else {
            this.userHasProfile = false;
          }
          
          resolved(this.userHasProfile && this.loggedIn() ? 1 : 0);
        }
      });
    });
  }

  // Appelé à chaque changement de texte dans la barre de recherche
  onSearchChange(searchText: string): void {
    this.searchQuery = searchText;
    if (searchText.trim().length > 0) {
      const searchLower = searchText.toLowerCase();
      
      Promise.all([
        getDocs(query(collection(this.firestore2, 'Posts'), limit(1000))),
        getDocs(query(collection(this.firestore2, 'Users'), limit(1000)))
      ]).then(([postsSnapshot, usersSnapshot]) => {
        const suggestions: SearchSuggestion[] = [];
        
        // Traitement des posts
        postsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          
          if (data.comment && typeof data.comment === 'string') {
            // Diviser le texte en mots
            const words = data.comment.split(/\s+/);
            
            for (let i = 0; i < words.length; i++) {
              const word = words[i];
              if (word.toLowerCase().startsWith(searchLower)) {
                // Prendre le mot suivant s'il existe
                const nextWord = words[i + 1] || '';
                const fullText = word + (nextWord ? ' ' + nextWord : '');
                
                suggestions.push({
                  type: 'post',
                  text: fullText,
                  data: {
                    postId: doc.id,
                    preview: data.comment,
                    searchTerm: fullText // Stocker le texte complet comme terme de recherche
                  }
                });
              }
            }
          }
        });

        // Traitement des utilisateurs (inchangé)
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          const searchFields = [
            userData.publicName,
            userData.username,
            userData.description,
            userData.ville
          ].filter(field => field && typeof field === 'string');

          searchFields.forEach(field => {
            if (field.toLowerCase().includes(searchLower)) {
              suggestions.push({
                type: 'user',
                text: userData.publicName || userData.username,
                data: {
                  userId: doc.id,
                  photoURL: userData.profilePhotoUrl || 'https://firebasestorage.googleapis.com/v0/b/lenali-21.appspot.com/o/profile2.png?alt=media&token=a24d9c4a-5daf-48c0-a265-88c4b0371f3c',
                  description: userData.description,
                  username: userData.username,
                  ville: userData.ville
                }
              });
            }
          });
        });

        this.searchSuggestions = suggestions
          .filter((v, i, a) => a.findIndex(t => (
            t.type === v.type && t.text === v.text
          )) === i)
          .slice(0, 5);
        
        this.showSuggestions = this.searchSuggestions.length > 0;
      });
    } else {
      this.searchSuggestions = [];
      this.showSuggestions = false;
    }
  }

  // Fonction pour gérer la sélection d'une suggestion
  selectSuggestion(suggestion: SearchSuggestion) {
    if (suggestion.type === 'post') {
      // Utiliser le texte complet de la suggestion pour la recherche
      this.router.navigate(['postfeed/search'], { 
        queryParams: { 
          q: suggestion.text, // Utiliser le texte complet ("maliens ont") au lieu du terme initial
          type: 'post'
        }
      });
      this.showSuggestions = false;
    } else if (suggestion.type === 'user') {
      // Navigation vers le profil utilisateur
      this.router.navigate(['postfeed/userprofile', suggestion.data.userId]);
      this.showSuggestions = false;
    }
  }

  highlightSearchTerm(text: string, searchTerm: string): string {
    if (!searchTerm) return text;
    const searchTermLower = searchTerm.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(searchTermLower);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + searchTerm.length);
    const after = text.slice(index + searchTerm.length);
    
    return `${before}<span class="highlight-search">${match}</span>${after}`;
  }

  toggleSearchBar() {
    this.searchBarVisible = !this.searchBarVisible;
    const searchBar = document.getElementById('search-bar');
    if (searchBar) {
      searchBar.style.display = this.searchBarVisible ? 'flex' : 'none';
    }
  }

  // Pour afficher/masquer le menu du profil
  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }

  // Méthodes pour les actions du menu du profil
  onProfileClick() {
    // Navigation vers la page de profil
  }

 

  onSearch(): void {
    if (this.searchQuery.trim().length > 0) {
      this.router.navigate(['/search'], { queryParams: { q: this.searchQuery } });
    }
  }

  
  loggedIn(){
    return true;
  }

  onLogoutClicka(){

   
    this.component.onLogoutClick();

    
  }

  onLogoutClick(){
    //alert(this.auth.getAuth().currentUser.uid);
    this.auth.signOut();
    this.authService.logout();
    alert("vous êtes deconnecté!");
    //this.router.navigate([""]);
    window.location.reload();
  }

  onCreatePostClick(){
    

    const isMobile = this.breakpointObserver.isMatched('(max-width: 480px)');
    this.dialog.open(CreatePostComponent, {
      width: isMobile ? '100vw' : '900px', // Largeur pleine pour mobile
      maxWidth: isMobile ? '100%' : '90%',
      minHeight: isMobile ? '450px' : '400px',
      panelClass: isMobile ? 'mobile-dialog' : ''
    });
  }

  getPosts(){

    
    //this.username = this.auth.getAuth().currentUser.uid;
    
    
    this.firestore.getCollection(
      {
        path: ["Users"],
        where: [
          
        ],
        
        onComplete: (result) => {
          //alert(result.docs.length);
          

          result.docs.forEach(
            doc => {
              
              if(doc.id == this.auth.getAuth().currentUser.uid){
                this.currentUser = <UserData>doc.data();
                this.username = this.currentUser.publicName;
                this.profession = this.currentUser.description;
                if(this.currentUser.profilePhotoUrl != ""){
                  this.profilePhotoUrl = this.currentUser.profilePhotoUrl;
                }
                //alert(this.username);
                
              }
              
              let post = <PostData>doc.data();
              post.postId = doc.id;
              this.posts.push(post);
            }
          );
        },
        onFail: err => {
          //alert("Echec de ");
        }
      }
    );
    
    this.firestore.getCollection(
      {
        path: ["Posts"],
        where: [
          new OrderBy("timestamp", "desc"),
          new Limit(10)
        ],
        onComplete: (result) => {
          result.docs.forEach(
            doc => {
              let post = <PostData>doc.data();
              post.postId = doc.id;
              this.posts.push(post);
            }
          );
        },
        onFail: err => {

        }
      }
    );
  }

  listenToChange(){
    this.firestore.listenToDocument(
      {
        name: "Getting Document",
        path: ["Users", this.auth.getAuth().currentUser.uid],
        onUpdate: (result) => {
          
          if(result.id == this.auth.getAuth().currentUser.uid){
            this.currentUser = <UserData>result.data();
            this.username = this.currentUser.publicName;
            this.profession = this.currentUser.description;
            if(this.currentUser.profilePhotoUrl != ""){
              this.profilePhotoUrl = this.currentUser.profilePhotoUrl;
            }
            //alert(this.username);
            
          }
          
          let post = <PostData>result.data();
          post.postId = result.id;
          this.posts.push(post); 
        }
      }
    );
  }

  getUserPosts() {
    this.firestore.getCollection({
      path: ["Posts"],
      where: [
        new Where("creatorId", "==", this.auth.getAuth().currentUser.uid)
      ],
      onComplete: (result) => {
        this.userPosts = result.docs.length;
        this.changeDetectorRef.detectChanges();
      },
      onFail: err => {
        console.error("Erreur lors de la récupération des posts:", err);
      }
    });
  }

  openDeleteDialog(postId: string): void {
    const dialogRef = this.dialog.open(DeletePostDialogComponent);
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deletePost(postId);
      }
    });
  }

  deletePost(postId: string): void {
    this.firestore.delete({
      path: ["Posts", postId],
      onComplete: () => {
        //alert("La publication a été supprimée avec succès !");
        location.reload();
      },
      onFail: (err) => {
        console.error("Erreur lors de la suppression : ", err);
      }
    });
  }

  goToProfile() {
    this.router.navigate(['/profile']);
  }
  
  


/** Mise en page pour les petits écrans */
  isMobileView: boolean = false;
  //profilePhotoUrl: string = 'path/to/default/profile/photo'; // Remplacez par votre source réelle
  //currentUser: any; // Remplacez par le type réel de currentUser

  searchBarVisible: boolean = false;
  showProfileMenu: boolean = false;

  isBottomBarVisible: boolean = true; // État de la barre inférieure

  

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkViewport();
  }

  private checkViewport() {
    this.isMobileView = window.innerWidth <= 768; // Ajustez la largeur pour ce que vous considérez comme mobile
   
  
  }

  goBack() {
    if (this.isProfileView) {
      this.router.navigate(['/postfeed/feed']);
    } else {
      window.history.back();
    }
  }
  /**Fin mise en page pour les petits écrans */

  onClickOutside(event: MouseEvent) {
    // Vérifier si le clic n'est pas sur un élément de suggestion
    const target = event.target as HTMLElement;
    if (!target.closest('.suggestion-item')) {
      this.showSuggestions = false;
    }
  }

  performSearch() {
    if (this.searchQuery.trim()) {
      // Naviguer vers la page de recherche avec le terme actuel
      this.router.navigate(['postfeed/search'], {
        queryParams: {
          q: this.searchQuery.trim(),
          type: 'all' // Changer de 'post' à 'all' pour inclure les utilisateurs
        }
      });
      this.showSuggestions = false;
    }
  }
}
